/**
 * LOT-UI-02 — Recherche globale (header + palette).
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Wrench, ShoppingCart, Users, Package, Building2, ScanLine, Smartphone } from 'lucide-react';
import QrScanner from '../QrScanner';

import { activiteVenteSearchUrl } from '../../utils/navigation/hubNavigation';

const TYPE_META = {
  repair: { icon: Wrench, label: 'Réparation', color: 'text-[var(--rm-red-light)] bg-[var(--rm-red-muted)]' },
  standard: { icon: Wrench, label: 'Réparation', color: 'text-[var(--rm-red-light)] bg-[var(--rm-red-muted)]' },
  b2b: { icon: Building2, label: 'B2B', color: 'text-[var(--rm-gray-300)] bg-white/10' },
  vente: { icon: ShoppingCart, label: 'Vente', color: 'text-[var(--rm-success-foreground)] bg-[var(--rm-success-muted)]' },
  client: { icon: Users, label: 'Client', color: 'text-[var(--rm-gray-300)] bg-white/10' },
  stock: { icon: Package, label: 'Stock', color: 'text-[var(--rm-success-foreground)] bg-[var(--rm-success-muted)]' },
  ticket: { icon: Wrench, label: 'Ticket', color: 'text-[var(--rm-red-light)] bg-[var(--rm-red-muted)]' },
};

function navigateResult(navigate, r) {
  if (r.url) {
    navigate(r.url);
    return;
  }
  const t = r.type;
  if (t === 'vente') navigate(activiteVenteSearchUrl(r.ref || r.title || ''));
  else if (t === 'client') navigate(r.ref ? `/clients?focus=${r.ref}` : '/clients');
  else if (t === 'stock') navigate(`/stock?search=${encodeURIComponent(r.ref || r.title || '')}`);
  else if (t === 'b2b') navigate('/admin/b2b');
  else if (r.ref) navigate(`/reparations/${r.ref}`);
}

const GlobalSearch = ({ api, compact = false, autoFocus = false, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setSearching(true);
    try {
      const res = await api.get(`/search/unified?q=${encodeURIComponent(q)}`);
      setResults(res.data.results || []);
      setOpen(true);
    } catch {
      try {
        const fallback = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(fallback.data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      }
    }
    setSearching(false);
  }, [api]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 280);
  };

  const handleSelect = (r) => {
    setOpen(false);
    setQuery('');
    navigateResult(navigate, r);
    onClose?.();
  };

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const grouped = results.reduce((acc, r) => {
    const key = TYPE_META[r.type]?.label || r.type || 'Autre';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  return (
    <div ref={wrapperRef} className={`relative ${compact ? 'w-full' : 'w-full max-w-md'}`} data-testid="global-search">
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={handleChange}
            onFocus={() => { if (results.length > 0) setOpen(true); }}
            placeholder="Ref, client, tel, article…"
            className="w-full pl-10 pr-10 py-2.5 min-h-[44px] rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[var(--rm-brand,#dc2626)]/50 transition-all"
            data-testid="global-search-input"
          />
          {query && (
            <button type="button" onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white" data-testid="global-search-clear">
              <X className="w-4 h-4" />
            </button>
          )}
          {searching && (
            <div className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
          )}
        </div>
        <button type="button" onClick={() => setShowScanner(true)}
          className="px-2.5 py-2 min-h-[44px] min-w-[44px] rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-[var(--theme-primary)] flex-shrink-0"
          title="Scanner QR" data-testid="global-search-scan">
          <ScanLine className="w-4 h-4" />
        </button>
      </div>

      {showScanner && (
        <QrScanner
          onScan={(ref) => {
            setShowScanner(false);
            const refUp = String(ref || '').toUpperCase();
            if (/^REM\d+$/.test(refUp)) {
              navigate(`/reparations/${refUp}`);
              onClose?.();
              return;
            }
            setQuery(refUp);
            doSearch(refUp);
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {open && (
        <div className="absolute z-[200] w-full mt-2 bg-[#1a1d24] border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-[min(420px,60vh)] overflow-y-auto"
          data-testid="global-search-results">
          {results.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">Aucun résultat</div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group}>
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-white/[0.02]">{group}</div>
                {items.map((r, i) => {
                  const meta = TYPE_META[r.type] || { icon: Smartphone, color: 'text-gray-400 bg-gray-500/20' };
                  const Icon = meta.icon;
                  const title = r.title || r.ref;
                  const sub = r.subtitle || [r.client_name, r.device, r.client_phone].filter(Boolean).join(' · ');
                  return (
                    <button key={`${r.type}-${title}-${i}`} type="button" onClick={() => handleSelect(r)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 text-left border-b border-white/5 last:border-0"
                      data-testid={`search-result-${i}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-white truncate">{title}</div>
                        {sub && <div className="text-xs text-gray-500 truncate">{sub}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
